import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import { isValid, parse } from 'date-fns'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { FieldType, scope } from './AttributeColsMod'
import {
  dataType,
  tsStudyAttributeOption,
} from '../../../Typescript/StuartPy/StudyAttribute'
import { Cancel, Clear, Edit, Error } from '@mui/icons-material'
import { ReadOnlyTextField } from '../../../garvan-react/Components/ReadOnlyTextField'
import { DatePicker } from '@mui/x-date-pickers'
import { useMutation, useQueryClient } from 'react-query'
import { AxiosResponse } from 'axios'
import { useParams } from 'react-router-dom'
import { usePhaseContext } from '../ConfigurePhase/PhaseAccordion'
import { useUserContext } from '../../../Contexts/UserConfig'
import { useMessagePopupContext } from '../../../garvan-react/Components/ModalFeedback/MessagePopup'
import { prepareDateSybase } from '../../../Functions/FormatValues'
import PyAPI from '../../../APIs/Python/APIs/PyAPI'
import FormulaModal from '../FormulaBuilder/FormulaModal'

export function AttributeInputGenerator({
  field,
  data,
  setData,
  errors,
  setErrors,
  values,
  modalType,
}: {
  field: FieldType
  data: any
  setData: any
  errors: any
  setErrors: any
  values?: any[]
  modalType: scope | undefined
}) {
  const { study_id } = useParams()
  const { whoAmI } = useUserContext()
  const { setError } = useMessagePopupContext()
  const queryClient = useQueryClient()
  const phase = usePhaseContext()

  const [openDetail, setDetail] = useState(false)
  const [options, setOptions] = useState<any>([])
  const [dataExists, setDataExists] = useState<string[]>([])
  const [dataExistsMessage, setDataExistsMessage] = useState('')

  let disabled = false
  if (field?.disabled) {
    if (typeof field.disabled === 'boolean') {
      disabled = field.disabled
    } else if (modalType) {
      disabled = field.disabled.includes(modalType)
    }
  }

  useEffect(() => {
    // Need to update attribute on phase refresh as that is a seperate API call for each change
    if (phase?.attributes) {
      const attribute = phase.attributes.filter(
        (a) => a.AttrID === data?.AttrID,
      )?.[0]
      if (attribute) {
        setData((d: any) => {
          return { ...d, options: attribute.options }
        })
      }
    }
  }, [phase?.attributes, data?.AttrID, setData])

  const { mutate: addOption } = useMutation<
    any,
    AxiosResponse,
    any,
    () => void
  >(
    (value) =>
      PyAPI.put(
        whoAmI,
        `/study/${study_id}/phase/${phase.PhaseID}/attribute/options?attr_id=${data.AttrID}`,
        JSON.stringify(value),
      ),
    {
      onError: (err) => setError(err),
    },
  )

  const { mutate: deleteOption } = useMutation<
    any,
    AxiosResponse,
    any,
    () => void
  >(
    (value) =>
      PyAPI.deleteCall(
        whoAmI,
        `/study/${study_id}/phase/${phase.PhaseID}/attribute/options?attr_id=${data.AttrID}&code_value=${value}`,
      ),
    {
      onError: (err, value) => {
        if (err.status === 406) {
          setDataExists((d) => [...d, value])
          setDataExistsMessage(err.data)
        } else {
          setError(err)
        }
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['PyAPI', '/study'],
        })
      },
    },
  )

  useEffect(() => {
    if (
      field.input === 'multichip' &&
      typeof data?.[field.field] !== 'string'
    ) {
      setOptions(data?.[field.field] as tsStudyAttributeOption[])
    }
  }, [field, data])

  switch (field.input) {
    case 'select':
      return (
        <Box
          sx={{ display: 'flex' }}
          key={field.field}
        >
          <FormControl
            fullWidth
            required={field.required}
          >
            <InputLabel>{field.headerName}</InputLabel>
            <Select
              label={field.headerName}
              name={field.field}
              value={data?.[field.field] || ''}
              onChange={(e) =>
                setData((d: any) => {
                  if (e.target.value === dataType.Endpoint) {
                    return { ...d, [e.target.name!]: e.target.value }
                  } else if (e.target.value === dataType['Calculated Field']) {
                    return {
                      ...d,
                      [e.target.name!]: e.target.value,
                      EndpointText: null, //not an EndpointType -> clear value
                    }
                  } else {
                    return {
                      ...d,
                      [e.target.name!]: e.target.value,
                      Formula: null, //not a Calc or Endpoint  -> clear value
                      EndpointText: null, //not an EndpointType -> clear value
                    }
                  }
                })
              }
              disabled={disabled}
            >
              {values}
            </Select>
          </FormControl>
          {!(field.required || disabled) && (
            <Box sx={{ p: 1 / 2, alignItems: 'center', display: 'flex' }}>
              <IconButton
                onClick={() =>
                  setData((d: any) => {
                    return { ...d, [field.field]: null }
                  })
                }
              >
                <Clear />
              </IconButton>
            </Box>
          )}
        </Box>
      )
    case 'multichip':
      return (
        <FormControl sx={{ width: 1, mt: 1, mb: 1 / 2 }}>
          <Autocomplete
            multiple
            renderInput={(params) => (
              <TextField
                {...params}
                label="Options (Autosave)"
              />
            )}
            onChange={async (event, newValue, reason, details) => {
              switch (reason) {
                case 'createOption':
                  if (details?.option) {
                    // Optimistic update
                    // nulls will be replaced with API response, otherwise option will be reset
                    setOptions((o: any) => [
                      ...o,
                      {
                        AttrCodeSetID: null,
                        CodeDescription: details.option,
                        CodeValue: null,
                      },
                    ])
                    addOption(details.option)
                  }
                  break
                case 'removeOption':
                  if (details?.option && options.length > 1) {
                    setOptions((o: any[]) =>
                      o.filter((i) => i.CodeValue !== details.option.CodeValue),
                    )
                    deleteOption(details.option.CodeValue)
                  }
                  break
                default:
                  console.log('error')
              }
            }}
            renderTags={(tagValue: tsStudyAttributeOption[], getTagProps) =>
              tagValue.map((option: tsStudyAttributeOption, index) => (
                <Tooltip
                  title={
                    dataExists.includes(option.CodeValue) ? dataExistsMessage
                    : ''
                  }
                  key={index}
                >
                  <span>
                    <Chip
                      label={option.CodeDescription}
                      {...getTagProps({ index })}
                      disabled={
                        dataExists.includes(option.CodeValue) ||
                        tagValue.length < 2
                      }
                      deleteIcon={
                        dataExists.includes(option.CodeValue) ?
                          <Error />
                        : <Cancel />
                      }
                    />
                  </span>
                </Tooltip>
              ))
            }
            options={[]}
            getOptionLabel={(option) => option.CodeDescription}
            freeSolo
            value={options || []}
            disableClearable
          />
          <FormHelperText>
            {options && options.length < 2 ?
              'At least one option must be available at all times'
            : ''}
          </FormHelperText>
          <FormHelperText>
            {options && options.length < 2 ?
              'Add a new option to enable deleting'
            : ''}
          </FormHelperText>
        </FormControl>
      )
    case 'date': {
      const parsed = parse(data?.[field.field], 'yyyy-MM-dd', new Date())
      const valid = isValid(parsed)
      return (
        <Box
          sx={{ display: 'flex' }}
          key={field.field}
        >
          <DatePicker
            label={field.headerName}
            value={valid ? parsed : null}
            onChange={(val) => {
              setErrors((d: any) => {
                return { ...d, [field.field]: undefined }
              })
              setData((d: any) => {
                return { ...d, [field.field]: val }
              })
            }}
            onAccept={(value) =>
              setData((d: any) => {
                //when picker is used
                if (field.required && !value) {
                  setErrors((d: any) => {
                    return {
                      ...d,
                      [field.field]: field.headerName + ' is a required field',
                    }
                  })
                }
                return { ...d, [field.field]: prepareDateSybase(value) }
              })
            }
            minDate={new Date('1900-01-01')}
            disableFuture
            sx={{ width: 1 }}
            // onBlur={(e) =>
            //   setData((d: any) => {
            //     //when date is typed
            //     if (field.required && e.target.value.length < 1) {
            //       setErrors((d: any) => {
            //         return {
            //           ...d,
            //           [field.field]: field.headerName + ' is a required field',
            //         }
            //       })
            //     } else if (e.target.value.length > 0) {
            //       //check input format
            //       let parsed
            //       if (/^.*\/.*\/[0-9]{4}$/g.test(e.target.value)) {
            //         parsed = parse(e.target.value, 'd/M/yyyy', new Date())
            //       } else {
            //         parsed = parse(e.target.value, 'd/M/yy', new Date())
            //       }
            //       const valid = isValid(parsed)
            //       if (!valid) {
            //         setErrors((d: any) => {
            //           return {
            //             ...d,
            //             [field.field]: 'This date cannot be parsed',
            //           }
            //         })
            //         return { ...d, [field.field]: null }
            //       } else {
            //         return {
            //           ...d,
            //           [field.field]: prepareDateSybase(parsed),
            //         }
            //       }
            //     } else {
            //       return { ...d, [field.field]: null }
            //     }
            //   })
            // }
            // error={Boolean(errors?.[field.field])}
            // helperText={errors?.[field.field]}
          />
          {!(field.required || disabled) && (
            <Box sx={{ p: 1 / 2, alignItems: 'center', display: 'flex' }}>
              <IconButton
                onClick={() => {
                  setData((d: any) => {
                    return { ...d, [field.field]: null }
                  })
                  setErrors((d: any) => {
                    return { ...d, [field.field]: undefined }
                  })
                }}
              >
                <Clear />
              </IconButton>
            </Box>
          )}
        </Box>
      )
    }
    case 'checkbox':
      return (
        <FormControlLabel
          key={field.field}
          label={field.headerName}
          labelPlacement="end"
          control={
            <Checkbox
              checked={data?.[field.field] === 'Y'}
              onChange={(e) =>
                setData((d: any) => {
                  return { ...d, [field.field]: e.target.checked ? 'Y' : 'N' }
                })
              }
              color={'success'}
            />
          }
          sx={{ my: 1 / 2, mr: 'auto' }}
        />
      )
    case 'multiline':
      return (
        <TextField
          key={field.field}
          multiline
          minRows={3}
          required={field.required}
          id="name"
          label={field.headerName}
          fullWidth
          value={data?.[field.field] || ''}
          disabled={disabled}
          onChange={(e) => {
            setErrors((d: any) => {
              return { ...d, [field.field]: undefined }
            })
            setData((d: any) => {
              return { ...d, [field.field]: e.target.value }
            })
          }}
          onBlur={(e) =>
            setData((d: any) => {
              if (field.required && e.target.value.length < 1) {
                setErrors((d: any) => {
                  return {
                    ...d,
                    [field.field]: field.headerName + ' is a required field',
                  }
                })
              }
              return { ...d, [field.field]: e.target.value }
            })
          }
          error={Boolean(errors?.[field.field])}
        />
      )
    case 'alphanumeric':
      return (
        <TextField
          required={field.required}
          key={field.field}
          id="name"
          label={field.headerName}
          fullWidth
          value={data?.[field.field] || ''}
          disabled={disabled}
          onChange={(e) => {
            setErrors((d: any) => {
              return { ...d, [field.field]: undefined }
            })
            setData((d: any) => {
              return { ...d, [field.field]: e.target.value }
            })
          }}
          onBlur={(e) => {
            if (field.required && e.target.value.length < 1) {
              setErrors((d: any) => {
                return {
                  ...d,
                  [field.field]: field.headerName + ' is a required field',
                }
              })
            } else if (!/^[0-9A-z]*$/g.test(e.target.value)) {
              setErrors((d: any) => {
                return {
                  ...d,
                  [field.field]:
                    field.headerName + ' can only contain letters & numbers',
                }
              })
            } else {
              setErrors((d: any) => {
                return { ...d, [field.field]: undefined }
              })
            }
            setData((d: any) => {
              return { ...d, [field.field]: e.target.value }
            })
          }}
          error={Boolean(errors?.[field.field])}
          helperText={errors?.[field.field]}
        />
      )
    case 'integer':
    case 'float':
      return (
        <TextField
          required={field.required}
          key={field.field}
          id="name"
          label={field.headerName}
          fullWidth
          value={data?.[field.field] || ''}
          disabled={disabled}
          onChange={(e) => {
            setErrors((d: any) => {
              return { ...d, [field.field]: undefined }
            })
            setData((d: any) => {
              return { ...d, [field.field]: e.target.value }
            })
          }}
          onBlur={(e) => {
            if (field.required && e.target.value.length < 1) {
              setErrors((d: any) => {
                return {
                  ...d,
                  [field.field]: field.headerName + ' is a required field',
                }
              })
            } else {
              if (field.input === 'integer') {
                if (!/^[0-9]*$/g.test(e.target.value)) {
                  setErrors((d: any) => {
                    return {
                      ...d,
                      [field.field]:
                        field.headerName + ' is a number (integer) field',
                    }
                  })
                } else {
                  setErrors((d: any) => {
                    return { ...d, [field.field]: undefined }
                  })
                }
              } else if (field.input === 'float') {
                if (!/^[0-9]*(?:\.[0-9]+)?$/g.test(e.target.value)) {
                  setErrors((d: any) => {
                    return {
                      ...d,
                      [field.field]:
                        field.headerName + ' is a number (double) field',
                    }
                  })
                } else {
                  setErrors((d: any) => {
                    return { ...d, [field.field]: undefined }
                  })
                }
              }
            }
            setData((d: any) => {
              return { ...d, [field.field]: e.target.value }
            })
          }}
          error={Boolean(errors?.[field.field])}
          helperText={errors?.[field.field]}
        />
      )
    case 'formula':
      return (
        <Box
          sx={{ display: 'flex', mt: 1 }}
          key={field.field}
        >
          <ReadOnlyTextField
            multiline
            minRows={2}
            label={field.headerName}
            fullWidth
            value={data?.[field.field] || ''}
            disabled
          />
          <Box sx={{ p: 1 / 2, alignItems: 'center', display: 'flex' }}>
            <IconButton onClick={() => setDetail(true)}>
              <Edit />
            </IconButton>
            <FormulaModal
              open={openDetail}
              setClose={() => {
                setDetail(false)
              }}
              setCommit={(val: any) => {
                setDetail(false)
                setData((d: any) => {
                  return { ...d, [field.field]: val }
                })
              }}
              data={data}
              field={field}
            />
          </Box>
        </Box>
      )
    default:
      return (
        <TextField
          required={field.required}
          key={field.field}
          id="name"
          label={field.headerName}
          fullWidth
          value={data?.[field.field] || ''}
          disabled={disabled || field.input === 'upload'}
          onChange={(e) => {
            setErrors((d: any) => {
              return { ...d, [field.field]: undefined }
            })
            setData((d: any) => {
              return { ...d, [field.field]: e.target.value }
            })
          }}
          onBlur={(e) =>
            setData((d: any) => {
              if (field.required && e.target.value.length < 1) {
                setErrors((d: any) => {
                  return {
                    ...d,
                    [field.field]: field.headerName + ' is a required field',
                  }
                })
              }
              return { ...d, [field.field]: e.target.value }
            })
          }
          error={Boolean(errors?.[field.field])}
        />
      )
  }
}
